import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PhotoboothService } from '../../services/photobooth.service';
import { StoreService } from '../../services/store.service';
import { STORAGE_DELETE_MESSAGES, classifyStorageUrl, deleteConfirmationFor } from '../../utils/storage-ownership.util';
import { CloneConfirmation, cloneConfirmation, cloneErrorMessage, cloneResultMessage } from '../../utils/clone-messages.util';
import { isGlobalRoute, STANDALONE_HOUSEKEEPING_MESSAGES } from '../../utils/standalone-housekeeping.util';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-photobooth-config-hub',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './photobooth-config-hub.component.html',
  styleUrls: ['./photobooth-config-hub.component.css']
})
export class PhotoboothConfigHubComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private photoboothService = inject(PhotoboothService);
  private storeService = inject(StoreService);
  private cdr = inject(ChangeDetectorRef);

  readonly housekeepingMessages = STANDALONE_HOUSEKEEPING_MESSAGES;

  storeId: string = '';
  deviceId: string = '';
  isGlobal: boolean = false;
  version: string | null = null;

  isLoading: boolean = true;
  deviceName: string = '';
  isTesting: boolean = false;
  isTestingSaved: boolean = false;
  isCustomized: boolean = false;

  // Device metadata
  appVersion: string = '';
  deviceOnline: boolean = false;
  cameraConnected: boolean = false;
  cardMachineConnected: boolean = false;
  billAcceptorConnected: boolean = false;
  useCard: boolean = true;
  useCash: boolean = true;
  lastPinged: any = null;

  // Store reassignment
  allActiveStores: any[] = [];
  selectedStoreId: string = '';
  isSavingStore: boolean = false;

  // Videos
  introVideos: string[] = [];
  printingVideos: any[] = [];
  isUploadingVideo: boolean = false;
  excludeGlobalVideos: boolean = false;
  isUpdatingExcludeGlobalVideos: boolean = false;

  // Layout Tile Summary
  tileCount: number = 0;
  customizedThemesCount: number = 0;

  // Standalone Layout & Cloning
  overrideGlobalLayout: boolean = false;
  hasExistingConfig: boolean = false;
  isUpdatingLayoutMode: boolean = false;
  isCloneModalOpen: boolean = false;
  cloneSourceSelection: 'resume' | 'global' | 'device' | 'blank' = 'global';
  cloneSourceDeviceId: string = '';
  availableCloneDevices: any[] = [];
  isCloningInProgress: boolean = false;
  /** KAN-518: confirmation step of the clone modal (null = option list is shown). */
  cloneConfirm: CloneConfirmation | null = null;

  // KAN-523: Revert to Global modal & Unsaved-changes publish warning modal
  isRevertModalOpen: boolean = false;
  isRevertingInProgress: boolean = false;
  isUnsavedPublishModalOpen: boolean = false;

  // Machine Status Email Notification Settings (Global)
  sendMachineStatusEmail: boolean = false;
  machineStatusEmailRecipients: string = '';
  machineStatusEmailHourPT: number = 8; // default 8:00 AM PST
  isSavingEmailSettings: boolean = false;
  isEmailSettingsSaved: boolean = false;
  isSendingTestEmail: boolean = false;
  testEmailFeedback: string = '';
  testEmailStatus: 'success' | 'error' | '' = '';
  pstHoursList: { hour: number; label: string }[] = [];

  generatePstHoursList(): { hour: number; label: string }[] {
    const list: { hour: number; label: string }[] = [];
    for (let h = 0; h < 24; h++) {
      let label = '';
      if (h === 0) {
        label = '12:00 AM PST (Midnight)';
      } else if (h === 12) {
        label = '12:00 PM PST (Noon)';
      } else if (h < 12) {
        label = `${h}:00 AM PST`;
      } else {
        label = `${h - 12}:00 PM PST`;
      }
      list.push({ hour: h, label });
    }
    return list;
  }

  async ngOnInit(): Promise<void> {
    this.pstHoursList = this.generatePstHoursList();
    this.route.params.subscribe(async params => {
      this.storeId = params['storeId'] || '';
      this.deviceId = params['deviceId'] || '';
      // KAN-523 AC-1: Single shared route predicate (storeId === 'global' && deviceId === 'global')
      this.isGlobal = isGlobalRoute(this.storeId, this.deviceId);
      this.selectedStoreId = this.storeId;

      this.route.queryParams.subscribe(async queryParams => {
        this.version = queryParams['version'] || null;
        await this.loadAllData();
      });
    });
  }

  async loadAllData(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.isGlobal) {
        const emailConfig = await this.photoboothService.getMachineStatusEmailConfig();
        this.sendMachineStatusEmail = emailConfig.sendMachineStatusEmail;
        this.machineStatusEmailRecipients = emailConfig.machineStatusEmailRecipients;
        this.machineStatusEmailHourPT = emailConfig.machineStatusEmailHourPT !== undefined ? emailConfig.machineStatusEmailHourPT : 8;
      } else {
        // Load stores for reassignment dropdown
        const stores = await firstValueFrom(this.storeService.getStores());
        this.allActiveStores = (stores || []).filter((s: any) => s.status !== 'inactive');

        // Load device meta
        const devDoc = await this.storeService.getDevice(this.deviceId);
        if (devDoc) {
          this.isTesting = !!devDoc.isTesting;
          this.deviceName = devDoc.name || devDoc.deviceName || this.deviceId;
          this.originalDeviceName = this.deviceName;
          this.appVersion = devDoc.appVersion || '';
          this.deviceOnline = !!devDoc.deviceOnline;
          this.cameraConnected = !!devDoc.cameraConnected;
          this.cardMachineConnected = devDoc.cardMachineConnected !== false;
          this.billAcceptorConnected = devDoc.billAcceptorConnected !== false;
          this.useCard = devDoc.useCard !== false;
          this.useCash = devDoc.useCash !== false;
          this.lastPinged = devDoc.lastSeen?.toDate ? devDoc.lastSeen.toDate() : devDoc.lastSeen;
          if (devDoc.storeId) {
            this.selectedStoreId = devDoc.storeId;
          }
        }

        // Check if device is customized
        this.isCustomized = await this.photoboothService.checkIfDeviceCustomized(this.deviceId, this.version);
        this.overrideGlobalLayout = await this.photoboothService.getDeviceOverrideGlobalLayout(this.deviceId, this.version);
        this.excludeGlobalVideos = await this.photoboothService.getExcludeGlobalVideos(this.isGlobal, this.deviceId, this.version);
        // KAN-523 AC-7: Show "Continue with previous settings" only when standaloneTiles canvas exists
        this.hasExistingConfig = await this.photoboothService.hasDeviceStandaloneCanvas(this.deviceId, this.version);

        try {
          const devices = await firstValueFrom(this.storeService.getAllDevicesLive());
          this.availableCloneDevices = (devices || []).filter((d: any) => {
            const id = d.deviceId || d.id;
            return id && id !== 'global' && id !== this.deviceId && !d.isDeleted;
          });
        } catch (devErr) {
          console.warn('Could not load available devices for cloning:', devErr);
        }
      }

      await Promise.all([
        this.loadVideos(),
        this.loadLayoutSummary()
      ]);
    } catch (e) {
      console.error('Error loading config hub data:', e);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  originalDeviceName: string = '';
  isSavingDeviceName: boolean = false;

  async saveDeviceName(): Promise<void> {
    if (this.isGlobal || !this.deviceId || this.version) return;
    const trimmed = (this.deviceName || '').trim();
    if (!trimmed) {
      alert('Device name cannot be empty.');
      return;
    }
    this.isSavingDeviceName = true;
    try {
      await this.storeService.updateDeviceName(this.deviceId, trimmed);
      this.deviceName = trimmed;
      this.originalDeviceName = trimmed;
      alert('Device name updated successfully.');
    } catch (e) {
      console.error('Failed to update device name:', e);
      alert('Failed to update device name.');
    } finally {
      this.isSavingDeviceName = false;
      this.cdr.detectChanges();
    }
  }

  async saveIsTesting(): Promise<void> {
    if (this.isGlobal || this.version) return;
    try {
      await this.storeService.updateDeviceTestingStatus(this.deviceId, this.isTesting);
      this.isTestingSaved = true;
      setTimeout(() => {
        this.isTestingSaved = false;
        this.cdr.detectChanges();
      }, 2000);
    } catch (e) {
      console.error('Failed to update isTesting', e);
      alert('Failed to update test machine status.');
    }
  }

  async saveStore(): Promise<void> {
    if (!this.selectedStoreId || this.selectedStoreId === this.storeId) return;
    this.isSavingStore = true;
    try {
      const storeObj = this.allActiveStores.find((s: any) => s.storeId === this.selectedStoreId);
      const storeDocId = storeObj ? storeObj.id || '' : '';
      await this.storeService.updateDeviceStore(this.deviceId, this.selectedStoreId, storeDocId);
      this.storeId = this.selectedStoreId;
      alert('Store updated successfully.');
    } catch (e) {
      console.error('Failed to update store:', e);
      alert('Failed to update store.');
    } finally {
      this.isSavingStore = false;
      this.cdr.detectChanges();
    }
  }

  openRevertModal(): void {
    if (this.isGlobal || this.version || this.isRevertingInProgress) return;
    this.isRevertModalOpen = true;
    this.cdr.detectChanges();
  }

  closeRevertModal(): void {
    if (this.isRevertingInProgress) return;
    this.isRevertModalOpen = false;
    this.cdr.detectChanges();
  }

  async revertToGlobal(): Promise<void> {
    this.openRevertModal();
  }

  async confirmRevertToGlobal(): Promise<void> {
    if (this.isGlobal || this.version || this.isRevertingInProgress) return;
    this.isRevertingInProgress = true;
    this.cdr.detectChanges();
    try {
      await this.photoboothService.revertDeviceToGlobal(this.deviceId);
      this.isCustomized = false;
      this.hasExistingConfig = false;
      this.overrideGlobalLayout = false;
      this.excludeGlobalVideos = false;
      this.isRevertModalOpen = false;
      alert(STANDALONE_HOUSEKEEPING_MESSAGES.revertSuccess);
      await Promise.all([
        this.loadVideos(),
        this.loadLayoutSummary()
      ]);
    } catch (error) {
      console.error('Failed to revert device to global:', error);
      alert('Failed to revert device. Check console for details.');
    } finally {
      this.isRevertingInProgress = false;
      this.cdr.detectChanges();
    }
  }

  async toggleExcludeGlobalVideos(): Promise<void> {
    if (this.isGlobal || this.version || this.isUpdatingExcludeGlobalVideos) return;
    this.isUpdatingExcludeGlobalVideos = true;
    const newValue = !this.excludeGlobalVideos;
    try {
      await this.photoboothService.setExcludeGlobalVideos(this.deviceId, newValue);
      this.excludeGlobalVideos = newValue;
    } catch (error) {
      console.error('Error setting excludeGlobalVideos:', error);
      alert('Failed to update global videos setting.');
    } finally {
      this.isUpdatingExcludeGlobalVideos = false;
      this.cdr.detectChanges();
    }
  }

  async toggleSendMachineStatusEmail(): Promise<void> {
    if (!this.isGlobal || this.version) return;
    await this.saveEmailSettings(false);
  }

  async saveEmailSettings(showAlert: boolean = true): Promise<void> {
    if (!this.isGlobal || this.version) return;
    this.isSavingEmailSettings = true;
    this.testEmailFeedback = '';
    this.testEmailStatus = '';
    try {
      await this.photoboothService.saveMachineStatusEmailConfig({
        sendMachineStatusEmail: this.sendMachineStatusEmail,
        machineStatusEmailRecipients: (this.machineStatusEmailRecipients || '').trim(),
        machineStatusEmailHourPT: Number(this.machineStatusEmailHourPT)
      });
      this.isEmailSettingsSaved = true;
      if (showAlert) {
        this.testEmailStatus = 'success';
        this.testEmailFeedback = 'Email settings saved successfully.';
        setTimeout(() => {
          if (this.testEmailFeedback === 'Email settings saved successfully.') {
            this.testEmailFeedback = '';
            this.testEmailStatus = '';
            this.cdr.detectChanges();
          }
        }, 3500);
      }
      setTimeout(() => {
        this.isEmailSettingsSaved = false;
        this.cdr.detectChanges();
      }, 3000);
    } catch (err) {
      console.error('Failed to save machine status email settings:', err);
      this.testEmailStatus = 'error';
      this.testEmailFeedback = 'Failed to save email settings.';
    } finally {
      this.isSavingEmailSettings = false;
      this.cdr.detectChanges();
    }
  }

  async sendQuickTestEmail(): Promise<void> {
    if (this.isSendingTestEmail) return;
    const rawEmails = (this.machineStatusEmailRecipients || '').trim();
    if (!rawEmails) {
      this.testEmailStatus = 'error';
      this.testEmailFeedback = 'Please enter at least one recipient email address.';
      return;
    }

    const emailList = rawEmails.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.length > 0 && e.includes('@'));
    if (emailList.length === 0) {
      this.testEmailStatus = 'error';
      this.testEmailFeedback = 'Please enter valid recipient email address(es).';
      return;
    }

    this.isSendingTestEmail = true;
    this.testEmailFeedback = 'Sending machine status test email...';
    this.testEmailStatus = '';
    this.cdr.detectChanges();

    try {
      await this.photoboothService.saveMachineStatusEmailConfig({
        sendMachineStatusEmail: this.sendMachineStatusEmail,
        machineStatusEmailRecipients: this.machineStatusEmailRecipients,
        machineStatusEmailHourPT: Number(this.machineStatusEmailHourPT)
      });

      await this.photoboothService.sendMachineStatusEmailTest(emailList);
      this.testEmailStatus = 'success';
      this.testEmailFeedback = `✓ Test email sent successfully to ${emailList.join(', ')}!`;
    } catch (err: any) {
      console.error('Error sending test email:', err);
      this.testEmailStatus = 'error';
      this.testEmailFeedback = `Failed to send test email: ${err.message || 'Unknown error'}`;
    } finally {
      this.isSendingTestEmail = false;
      this.cdr.detectChanges();
    }
  }

  async loadVideos(): Promise<void> {
    try {
      this.introVideos = await this.photoboothService.getVideos(this.isGlobal, this.deviceId, 'intro', this.version);
      const rawPrinting = await this.photoboothService.getVideos(this.isGlobal, this.deviceId, 'printing', this.version);
      this.printingVideos = rawPrinting.map(v => typeof v === 'string' ? { videoUrl: v } : v);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  }

  extractVideoName(itemOrUrl: any): string {
    const url = typeof itemOrUrl === 'string' ? itemOrUrl : (itemOrUrl?.videoUrl || itemOrUrl?.name || '');
    if (!url) return 'Unknown Video';
    try {
      const urlObj = new URL(url);
      let path = decodeURIComponent(urlObj.pathname);
      let filename = path.split('/').pop() || 'Unknown Video';
      if (filename.includes('_')) {
        const parts = filename.split('_');
        if (!isNaN(Number(parts[0])) && parts[0].length >= 10) {
          parts.shift();
          filename = parts.join('_');
        }
      }
      return filename;
    } catch (e) {
      return 'Unknown Video';
    }
  }

  extractBackgroundName(item: any): string {
    const url = item?.backgroundUrl || '';
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      let path = decodeURIComponent(urlObj.pathname);
      let filename = path.split('/').pop() || '';
      if (filename.includes('_')) {
        const parts = filename.split('_');
        if (!isNaN(Number(parts[0])) && parts[0].length >= 10) {
          parts.shift();
          filename = parts.join('_');
        }
      }
      return filename;
    } catch (e) {
      return 'Background Image';
    }
  }

  getVideoUrl(item: any): string {
    return typeof item === 'string' ? item : (item?.videoUrl || '');
  }

  async uploadIntroVideo(event: any): Promise<void> {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    this.isUploadingVideo = true;
    try {
      await this.photoboothService.uploadVideo(this.isGlobal, this.deviceId, 'intro', files[0]);
      await this.loadVideos();
    } catch (error) {
      console.error(`Error uploading intro video:`, error);
      alert(`Failed to upload intro video.`);
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
      event.target.value = '';
    }
  }

  async uploadPrintingMedia(event: any): Promise<void> {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    this.isUploadingVideo = true;
    try {
      await this.photoboothService.uploadPrintingMediaItem(this.isGlobal, this.deviceId, files[0]);
      await this.loadVideos();
    } catch (error) {
      console.error(`Error uploading printing video:`, error);
      alert(`Failed to upload printing video.`);
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
      event.target.value = '';
    }
  }

  async uploadBackgroundForPrintingVideo(event: any, vidItem: any): Promise<void> {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const videoUrl = this.getVideoUrl(vidItem);
    this.isUploadingVideo = true;
    try {
      await this.photoboothService.updatePrintingMediaItemBg(this.isGlobal, this.deviceId, videoUrl, files[0]);
      await this.loadVideos();
    } catch (error) {
      console.error('Error uploading background for printing video:', error);
      alert('Failed to upload background image for printing video.');
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
      event.target.value = '';
    }
  }

  async deleteBackgroundForPrintingVideo(vidItem: any): Promise<void> {
    const videoUrl = this.getVideoUrl(vidItem);
    const bgOwnership = classifyStorageUrl(vidItem?.backgroundUrl, this.isGlobal, this.deviceId);
    if (!confirm(deleteConfirmationFor(bgOwnership))) return;
    this.isUploadingVideo = true;
    try {
      const outcome = await this.photoboothService.removePrintingMediaItemBg(this.isGlobal, this.deviceId, videoUrl);
      await this.loadVideos();
      if (outcome === 'reference-only') alert(STORAGE_DELETE_MESSAGES.resultReferenceRemoved);
    } catch (error) {
      console.error('Error removing background for printing video:', error);
      alert('Failed to remove background image.');
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
    }
  }

  async deleteVideo(type: 'intro' | 'printing', videoItemOrUrl: any): Promise<void> {
    const videoUrl = typeof videoItemOrUrl === 'string' ? videoItemOrUrl : (videoItemOrUrl?.videoUrl || '');
    if (!confirm(deleteConfirmationFor(classifyStorageUrl(videoUrl, this.isGlobal, this.deviceId)))) return;
    this.isUploadingVideo = true;
    try {
      const outcome = await this.photoboothService.deleteVideo(this.isGlobal, this.deviceId, type, videoUrl);
      await this.loadVideos();
      if (outcome === 'reference-only') alert(STORAGE_DELETE_MESSAGES.resultReferenceRemoved);
    } catch (error) {
      console.error(`Error deleting ${type} video:`, error);
      alert(`Failed to delete ${type} video.`);
    } finally {
      this.isUploadingVideo = false;
      this.cdr.detectChanges();
    }
  }

  async loadLayoutSummary(): Promise<void> {
    try {
      const tiles = this.isGlobal
        ? await this.photoboothService.getGlobalLayout(this.version)
        : await this.photoboothService.getDeviceLayout(this.deviceId, this.version);
      
      this.tileCount = tiles ? tiles.length : 0;
      if (this.overrideGlobalLayout) {
        this.customizedThemesCount = this.tileCount;
      } else {
        this.customizedThemesCount = tiles ? tiles.filter((t: any) => t.properties?.isCustomized).length : 0;
      }
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error loading layout summary:', e);
    }
  }

  async handleLayoutModeChange(targetMode: 'global' | 'standalone'): Promise<void> {
    if (this.isGlobal || this.version || this.isUpdatingLayoutMode) return;
    
    if (targetMode === 'global') {
      if (confirm(`Are you sure you want to switch ${this.deviceName || this.deviceId} to Inherited Global Layout Mode? The machine will resume inheriting the fleet-wide global layout. (Your custom device layout and assets are preserved and will remain saved if you switch back to Standalone Mode).`)) {
        this.isUpdatingLayoutMode = true;
        try {
          await this.photoboothService.setDeviceOverrideGlobalLayout(this.deviceId, false);
          this.overrideGlobalLayout = false;
          alert(`${this.deviceName || this.deviceId} is now set to Inherited Global Layout Mode.`);
          await this.loadLayoutSummary();
        } catch (err) {
          console.error('Failed to set inherited layout mode:', err);
          alert('Failed to update layout mode.');
        } finally {
          this.isUpdatingLayoutMode = false;
          this.cdr.detectChanges();
        }
      }
    } else if (targetMode === 'standalone') {
      this.openCloneModal();
    }
  }

  openCloneModal(): void {
    if (this.isGlobal || this.version) return;
    if (this.hasExistingConfig) {
      this.cloneSourceSelection = 'resume';
    } else {
      this.cloneSourceSelection = 'global';
    }
    this.cloneSourceDeviceId = this.availableCloneDevices.length > 0 ? (this.availableCloneDevices[0].deviceId || this.availableCloneDevices[0].id) : '';
    this.cloneConfirm = null;
    this.isCloneModalOpen = true;
    this.cdr.detectChanges();
  }

  closeCloneModal(): void {
    if (this.isCloningInProgress) return;
    this.cloneConfirm = null;
    this.isCloneModalOpen = false;
    this.cdr.detectChanges();
  }

  async confirmCloneLayout(): Promise<void> {
    if (this.isGlobal || this.version || this.isCloningInProgress) return;

    if (this.cloneSourceSelection === 'device' && !this.cloneSourceDeviceId) {
      alert('Please select a source photobooth machine to clone from.');
      return;
    }

    if (this.cloneSourceSelection === 'global' || this.cloneSourceSelection === 'device') {
      await this.runCloneStep(this.cloneSourceSelection);
      return;
    }

    if (this.cloneSourceSelection === 'blank') {
      // KAN-523 AC-6: Blank start confirmation stating existing frame groups and files are kept
      if (!confirm(STANDALONE_HOUSEKEEPING_MESSAGES.blankStartConfirm)) {
        return;
      }
    }

    this.isCloningInProgress = true;
    try {
      if (this.cloneSourceSelection === 'resume') {
        // KAN-523 AC-2: Simply re-enable Standalone Device Mode without overwriting previous layout or frame assets
        await this.photoboothService.setDeviceOverrideGlobalLayout(this.deviceId, true);
      } else if (this.cloneSourceSelection === 'blank') {
        await this.photoboothService.saveDeviceLayout(this.deviceId, [], true);
      }

      this.overrideGlobalLayout = true;
      this.hasExistingConfig = true;
      this.isCloneModalOpen = false;
      alert(`Successfully configured Standalone Device Layout for ${this.deviceName || this.deviceId}!`);
      await this.loadLayoutSummary();
    } catch (err: any) {
      console.error('Error cloning device configuration:', err);
      alert(`Failed to clone layout: ${err.message || 'Unknown error'}`);
    } finally {
      this.isCloningInProgress = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * KAN-518 clone flow: the first click asks the function what the machine already holds (no writes) and shows the confirmation
   * ("Copy", or "Replace layout" when data would be replaced); the second click copies. Clone from Machine is confirmed too and
   * states that sessions and logs are not copied (KAN-521).
   */
  private async runCloneStep(source: 'global' | 'device'): Promise<void> {
    const sourceId = source === 'global' ? 'global' : this.cloneSourceDeviceId;
    this.isCloningInProgress = true;
    this.cdr.detectChanges();
    try {
      let replace = false;
      if (this.cloneConfirm) {
        replace = this.cloneConfirm.replace;
      } else {
        const state = await this.photoboothService.inspectCloneTarget(sourceId, this.deviceId);
        this.cloneConfirm = cloneConfirmation(source, state, this.cloneSourceName(sourceId));
        return;
      }

      const result = await this.photoboothService.cloneDeviceConfiguration(sourceId, this.deviceId, replace);
      this.cloneConfirm = null;
      this.overrideGlobalLayout = true;
      this.hasExistingConfig = true;
      this.isCloneModalOpen = false;
      alert(cloneResultMessage(result, source));
      await this.loadLayoutSummary();
    } catch (err: any) {
      console.error('Error cloning device configuration:', err);
      this.cloneConfirm = null;
      alert(cloneErrorMessage(err?.message));
    } finally {
      this.isCloningInProgress = false;
      this.cdr.detectChanges();
    }
  }

  /** Display name of the clone source machine, as the source selector shows it. */
  private cloneSourceName(sourceId: string): string {
    const dev = this.availableCloneDevices.find(d => (d.deviceId || d.id) === sourceId);
    return dev?.deviceName || dev?.name || sourceId;
  }

  /** Leaves the clone confirmation without writing anything and returns to the option list. */
  cancelCloneConfirm(): void {
    if (this.isCloningInProgress) return;
    this.cloneConfirm = null;
    this.cdr.detectChanges();
  }

  isPublishingDevice: boolean = false;

  async publishToDevice(): Promise<void> {
    if (this.isGlobal || !this.deviceId || this.version) return;
    // KAN-523 AC-11: Warn when the editor has unsaved layout changes for this device
    if (this.photoboothService.hasUnsavedLayoutChanges(this.deviceId)) {
      this.isUnsavedPublishModalOpen = true;
      this.cdr.detectChanges();
      return;
    }
    const displayName = this.deviceName || this.deviceId;
    if (!confirm(`Are you sure you want to publish the current configuration directly to ${displayName}?`)) {
      return;
    }
    await this.executePublishToDevice();
  }

  cancelUnsavedPublishModal(): void {
    this.isUnsavedPublishModalOpen = false;
    this.cdr.detectChanges();
  }

  async confirmUnsavedPublishModal(): Promise<void> {
    this.isUnsavedPublishModalOpen = false;
    await this.executePublishToDevice();
  }

  private async executePublishToDevice(): Promise<void> {
    if (this.isGlobal || !this.deviceId || this.version) return;
    const displayName = this.deviceName || this.deviceId;
    this.isPublishingDevice = true;
    try {
      await this.photoboothService.publishDeviceConfig(this.deviceId);
      alert(`Successfully published to ${displayName}!`);
    } catch (error) {
      console.error('Publishing device failed', error);
      alert('Failed to publish to device. See console for details.');
    } finally {
      this.isPublishingDevice = false;
      this.cdr.detectChanges();
    }
  }

  openLayoutEditor(): void {
    const queryParams = this.version ? { version: this.version } : {};
    const targetStore = this.isGlobal ? 'global' : this.storeId;
    const targetDevice = this.isGlobal ? 'global' : this.deviceId;
    this.router.navigate(['/home/photobooth-management/layout-editor', targetStore, targetDevice], { queryParams });
  }

  goBack(): void {
    this.router.navigate(['/home/photobooth-management']);
  }
}
